import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Plus, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

const CATEGORIES = ["office", "travel", "conveyance", "electricity", "internet", "salary", "rent",
  "repairs", "maintenance", "transport", "fuel", "marketing", "professional_fees", "miscellaneous"];
const MODES = ["cash", "bank_transfer", "upi", "cheque", "card", "other"];
const EMPTY = { date: new Date().toISOString().slice(0, 10), category: "office", description: "",
  amount: "", tax: 0, mode: "cash", vendor: "", employee: "" };

export default function Expenses() {
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("all");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const qs = status !== "all" ? `?status=${status}` : "";
    api.get(`/expenses${qs}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/expenses/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId, status]); // eslint-disable-line

  const save = async () => {
    if (!form.description.trim() || !(+form.amount > 0)) { toast.error("Description and amount are required"); return; }
    setBusy(true);
    try {
      await api.post("/expenses", { ...form, amount: +form.amount, tax: +form.tax || 0 });
      toast.success("Expense submitted for approval");
      setDialog(false); setForm(EMPTY); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const decide = async (e, what) => {
    try {
      await api.post(`/expenses/${e.id}/${what}`);
      toast.success(`Expense ${e.expense_no} ${what}d`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="expenses-page">
      <PageHeader title="Expenses" testId="expenses-title" sub="Company spending with approval workflow">
        <Button data-testid="new-expense-btn" onClick={() => setDialog(true)}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Expense</Button>
      </PageHeader>

      {summary && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="expense-summary">
          {["pending", "approved", "rejected"].map((s) => (
            <span key={s} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs">
              <span className="capitalize text-slate-500">{s}:</span>{" "}
              <b className="font-mono">{summary.by_status?.[s]?.count || 0}</b>
              <span className="text-slate-400"> · </span>
              <Money value={summary.by_status?.[s]?.total || 0} className="font-semibold" />
            </span>
          ))}
        </div>
      )}

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="mb-4">
          {["all", "pending", "approved", "rejected"].map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`expenses-filter-${s}`} className="capitalize">{s}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="expenses-empty" title="No expenses" body="Record an expense — it starts as pending until approved." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">No</th><th className="p-3">Date</th><th className="p-3">Category</th>
                <th className="p-3">Description</th><th className="p-3">Vendor</th><th className="p-3 text-right">Amount</th>
                <th className="p-3 text-right">Tax</th><th className="p-3 text-right">Total</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
              <tbody>
                {data.items.map((e) => (
                  <tr key={e.id} data-testid={`expense-row-${e.expense_no}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="p-3 font-mono text-xs">{e.expense_no}</td>
                    <td className="p-3">{fmtDate(e.date)}</td>
                    <td className="p-3 capitalize text-slate-500">{e.category.replace(/_/g, " ")}</td>
                    <td className="p-3 max-w-[220px] truncate">{e.description}</td>
                    <td className="p-3 text-slate-500">{e.vendor || "—"}</td>
                    <td className="p-3 text-right"><Money value={e.amount} /></td>
                    <td className="p-3 text-right"><Money value={e.tax} className="text-slate-400" /></td>
                    <td className="p-3 text-right"><Money value={e.total} className="font-semibold" /></td>
                    <td className="p-3"><StatusBadge status={e.status.charAt(0).toUpperCase() + e.status.slice(1)} /></td>
                    <td className="p-3">
                      {e.status === "pending" && (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" className="text-emerald-600 h-7" data-testid={`expense-approve-${e.expense_no}`} onClick={() => decide(e, "approve")}><Check className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-500 h-7" data-testid={`expense-reject-${e.expense_no}`} onClick={() => decide(e, "reject")}><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent aria-describedby={undefined} className="max-w-lg bg-white dark:bg-slate-900" data-testid="expense-dialog">
          <DialogHeader><DialogTitle className="font-display">New Expense</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Date</Label><Input data-testid="expense-date" type="date" className="mt-1.5" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="expense-category" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Description *</Label><Input data-testid="expense-description" className="mt-1.5" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What was this spend for?" /></div>
            <div><Label>Amount (₹) *</Label><Input data-testid="expense-amount" type="number" min="0" className="mt-1.5" value={form.amount} onChange={(e) => set("amount", e.target.value)} /></div>
            <div><Label>GST/Tax (₹)</Label><Input type="number" min="0" className="mt-1.5" value={form.tax} onChange={(e) => set("tax", e.target.value)} /></div>
            <div><Label>Payment Mode</Label>
              <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
                <SelectTrigger data-testid="expense-mode" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Vendor</Label><Input className="mt-1.5" value={form.vendor} onChange={(e) => set("vendor", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button data-testid="expense-save-btn" onClick={save} disabled={busy}
              className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Submit Expense"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
